import { useCallback, useEffect, useRef } from "react";
import { LiveSessionError, saveLiveSessions } from "../live/liveHelper";
import { VerityApi } from "../../api/fetch";
import { convertPartialOptionsToNonNullable, equalProctoringOptions } from "@verity/proctoring-option";
import { EditQuizPageProps, EditQuizRef } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";
import { isThirdPartyStepValid, isValidPlatform } from "../editQuizHelper";
import i18nPackage from "../../i18n/i18n";
import { SAVE_EDIT_QUIZ_PROMPT } from "../../setup-guide/QuickSetupText";

type UseEditQuizSubmitProps = Pick<
	EditQuizPageProps,
	| "quizInfo"
	| "logger"
	| "proctoringAssessmentConfiguration"
	| "proctoringOption"
	| "setCurrentProfileId"
	| "currentProfileId"
	| "onSaveOptions"
	| "onStartSaveOptions"
	| "onSaveEnabledChange"
	| "enforcedProfilesOnly"
	| "onClose"
	| "quizType"
> &
	Pick<UseEditQuizStateReturn, "profile" | "student" | "theme" | "live" | "setting" | "submit"> & {
		api: VerityApi;
	};

/**
 * Handles logic related to saving the quiz AND the close button is here too because not sure where to put it
 * originally in editQuiz.ts
 */
export default function useEditQuizSubmit({
	quizInfo,
	proctoringAssessmentConfiguration,
	proctoringOption,
	logger,
	onSaveOptions,
	onStartSaveOptions,
	api,
	setCurrentProfileId,
	currentProfileId,
	onSaveEnabledChange,
	enforcedProfilesOnly,
	onClose,
	quizType,
	...props
}: UseEditQuizSubmitProps) {
	const { addAssessmentId, saveReferenceMaterials } = props.setting.action;
	const {
		assessmentUuids,
		verityInstituteSettings,
		proctoringData,
		isSetupGuideWorkflow,
		loading,
		referenceMaterialFiles,
		previousReferenceMaterialFiles,
	} = props.setting.state;
	const { isUpdateCurrentProfileClicked, activeProfile } = props.profile.state;
	const { isExemptedStudentsModified, accommodationView, saveDraftAccommodationRef, isDraftInvalid } =
		props.student.state;
	const { handleSaveExemptedStudentsNotStandalone } = props.student.action;
	const { themeExists } = props.theme.state;
	const { validateLiveSession, fetchAndSetExistingLiveSessions } = props.live.action;
	const { existingLiveSessions, pendingLiveSessions, pendingLiveSessionIds } = props.live.state;
	const {
		resetSaveStates,
		completePresave,
		saveProctoringConfiguration,
		confirmSave,
		confirmClose,
		openDisclaimerModal,
		initiateSaveProcess,
	} = props.submit.action;
	const {
		callReset,
		saveParameters,
		savePromise,
		finishedPresave,
		saveRequested,
		isAnyProctoringOptionEnabled,
		isAssessmentConfigurationModified,
		isProctoringOptionModified,
	} = props.submit.state;

	useEffect(() => {
		if (callReset) {
			resetSaveStates();
		}
	}, [callReset, resetSaveStates]);

	const saveLiveSessionsHandleErrors = useCallback(
		async (newQuizId?: string) => {
			if (assessmentUuids.current) {
				try {
					const assessmentUuidsResult = await addAssessmentId({ ...assessmentUuids.current, newQuizId });
					if (!assessmentUuidsResult) {
						throw new Error("No assessment UUIDs");
					}

					await saveLiveSessions({
						api,
						assessmentUuids: assessmentUuidsResult,
						existingSessions: existingLiveSessions,
						pendingSessions: pendingLiveSessions,
					});
				} finally {
					// update existing sessions for diff calculation
					await fetchAndSetExistingLiveSessions(assessmentUuids.current);
				}
			} else if (pendingLiveSessionIds.length > 0) {
				throw new Error("No assessment UUIDs");
			}
		},
		[
			addAssessmentId,
			api,
			assessmentUuids,
			existingLiveSessions,
			fetchAndSetExistingLiveSessions,
			pendingLiveSessionIds.length,
			pendingLiveSessions,
		],
	);

	const defaultSave = useCallback(async () => {
		const currentQuizId = quizInfo.quizId;
		if (!currentQuizId) {
			onStartSaveOptions?.({
				...proctoringOption,
				profileId: activeProfile?.id ?? currentProfileId,
				pendingLiveSessions,
				existingReferenceMaterialFiles: referenceMaterialFiles,
			});
			onSaveOptions(true, undefined);
			return;
		}

		/* Define the functions error handler */
		const logError = (e: unknown) => {
			console.error("Failed to save verity option ", e);
			void logger?.error(e);
			if (e instanceof LiveSessionError) {
				onSaveOptions(false, e.messages);
			} else {
				onSaveOptions(false, [i18nPackage.t("editQuizPage.failToSave")]);
			}
			resetSaveStates();
		};

		/* Destructure passed parameters */
		const { password, additionalAssessmentInfo } = saveParameters;

		// if options are different from profile, prompt choice between option or profile
		if (
			proctoringOption &&
			activeProfile &&
			!equalProctoringOptions(convertPartialOptionsToNonNullable(proctoringOption), activeProfile.proctoringOption)
		) {
			onStartSaveOptions?.();
			if (!isUpdateCurrentProfileClicked) {
				setCurrentProfileId(undefined);
			}
			try {
				const { options, result } = await saveProctoringConfiguration(
					password,
					additionalAssessmentInfo,
					false,
					!isUpdateCurrentProfileClicked ? true : false,
				);
				await handleSaveExemptedStudentsNotStandalone(currentQuizId);
				await saveLiveSessionsHandleErrors();
				await saveReferenceMaterials();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, options, result);
			} catch (e) {
				logError(e);
			}
		} else {
			onStartSaveOptions?.();
			try {
				const { options, result } = await saveProctoringConfiguration(password, additionalAssessmentInfo);
				await handleSaveExemptedStudentsNotStandalone(currentQuizId);
				await saveLiveSessionsHandleErrors();

				await saveReferenceMaterials();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, options, result);
			} catch (e) {
				logError(e);
			}
		}

		/* Resolve the promise passed back to parents */
		savePromise.resolve();
	}, [
		activeProfile,
		currentProfileId,
		handleSaveExemptedStudentsNotStandalone,
		isUpdateCurrentProfileClicked,
		logger,
		onSaveOptions,
		onStartSaveOptions,
		pendingLiveSessions,
		proctoringOption,
		quizInfo.quizId,
		resetSaveStates,
		saveLiveSessionsHandleErrors,
		saveParameters,
		saveProctoringConfiguration,
		savePromise,
		setCurrentProfileId,
		saveReferenceMaterials,
	]);

	// Ref so useEffect below has up to date variables and so typescript doesn't complain about unused dependencies
	const saveRequestRoutingRef = useRef({
		quizInfo,
		proctoringAssessmentConfiguration,
		themeExists,
		completePresave,
		openDisclaimerModal,
		defaultSave,
		quizType,
	});

	saveRequestRoutingRef.current = {
		quizInfo,
		proctoringAssessmentConfiguration,
		themeExists,
		completePresave,
		openDisclaimerModal,
		defaultSave,
		quizType,
	};

	/* Save Request Routing */
	useEffect(() => {
		/* If save has been requested */
		if (saveRequested) {
			/* Initiate save if presave steps are complete */
			if (finishedPresave) {
				const { defaultSave: latestDefaultSave } = saveRequestRoutingRef.current;
				void latestDefaultSave();
			} else {
				const {
					completePresave: latestCompletePresave,
					quizType: latestQuizType,
					openDisclaimerModal: latestOpenDisclaimer,
					proctoringAssessmentConfiguration: latestAssessmentConfig,
					quizInfo: latestQuizInfo,
					themeExists: latestThemeExists,
				} = saveRequestRoutingRef.current;

				/* Otherwise, initiate presave steps */
				switch (latestQuizInfo.lms) {
					case "canvas":
						if (
							!!latestAssessmentConfig?.thirdPartyAssessmentOption ||
							latestQuizType === "standalone" ||
							latestThemeExists
						) {
							latestCompletePresave();
						} else {
							latestOpenDisclaimer();
						}
						break;
					default:
						latestCompletePresave();
						break;
				}
			}
		}
	}, [saveRequested, finishedPresave]);

	/**
	 * This function is awaited by 2 parent wrappers on clicking submit
	 * The parents upon click of submit also execute other internal logic
	 *
	 * Hence, we must perform any logic that is to be executed on save request
	 * while halting the parent call
	 *
	 * Manually create and return a promise to the parent on call, and
	 * resolve / reject upon completion
	 *
	 */
	const saveButtonHandler: EditQuizRef["saveButtonHandler"] = useCallback(
		async (password, additionalAssessmentInfo) => {
			if (!isValidPlatform({ option: proctoringOption, verityInstituteSettings, quizType })) return false;
			// On the accommodation "step" which is on the review page,
			// when the user is selecting accommodaitions, they click "Save" to apply changes
			if (accommodationView === "select" && saveDraftAccommodationRef.current) {
				saveDraftAccommodationRef.current();
				return false;
			}

			// Since progress bar is non linear, it is possible for user to create a live session, click back button (skipping the validation), then jump to confirmation step.
			// Intentionally did not add validation check to back button because if user wants to switch method to automated in step 1,
			// they must delete a live session in step 2, but if validation blocks them from leaving step 2 cuz they didn't create a live session, it creates an infinite loop
			if (proctoringAssessmentConfiguration?.isLiveProctored && !validateLiveSession()) return false;

			if (!isAnyProctoringOptionEnabled) {
				const userConfirmed = await confirmSave();
				if (!userConfirmed) {
					return false;
				}
			}
			initiateSaveProcess({ additionalAssessmentInfo, password });
			return true;
		},
		[
			accommodationView,
			saveDraftAccommodationRef,
			proctoringAssessmentConfiguration?.isLiveProctored,
			validateLiveSession,
			isAnyProctoringOptionEnabled,
			initiateSaveProcess,
			confirmSave,
			proctoringOption,
			verityInstituteSettings,
			quizType,
		],
	);

	const closeButtonHandler: EditQuizRef["closeButtonHandler"] = useCallback(async () => {
		if (isAssessmentConfigurationModified || isProctoringOptionModified || isExemptedStudentsModified) {
			const userConfirmed = await confirmClose();
			if (!userConfirmed) {
				return false;
			}
		}

		// Only the settings to restore are handed back: passing the current proctoring option here would
		// make the caller adopt the very changes the user just chose to discard
		onClose?.({
			existingReferenceMaterialFiles: previousReferenceMaterialFiles,
		});

		return true;
	}, [
		confirmClose,
		isAssessmentConfigurationModified,
		isExemptedStudentsModified,
		isProctoringOptionModified,
		onClose,
		previousReferenceMaterialFiles,
	]);

	// Handles validation of save button
	useEffect(() => {
		// Live session not okay if user chose live session option but did not create a live session
		const liveSessionOk = !proctoringAssessmentConfiguration?.isLiveProctored || pendingLiveSessionIds.length > 0;
		const thirdPartyOk = isThirdPartyStepValid(proctoringData.thirdPartyPlatforms, proctoringAssessmentConfiguration);

		const hideSaveButton = accommodationView === "table" || accommodationView === "details";

		const saveEnabled =
			liveSessionOk &&
			thirdPartyOk &&
			(!enforcedProfilesOnly || Boolean(activeProfile?.id)) &&
			isValidPlatform({
				option: proctoringOption,
				verityInstituteSettings: verityInstituteSettings,
				quizType,
			}) &&
			!isDraftInvalid &&
			!loading;

		const accommodationText =
			accommodationView === "select" ? i18nPackage.t("editQuizPage.accommodationModal.saveAccommodations") : undefined;

		const shouldHighlight = isSetupGuideWorkflow && !hideSaveButton && saveEnabled && !accommodationText && !loading;

		onSaveEnabledChange?.({
			enabled: saveEnabled,
			text: accommodationText,
			hide: hideSaveButton,
			highlightText: shouldHighlight ? SAVE_EDIT_QUIZ_PROMPT : undefined,
		});
	}, [
		onSaveEnabledChange,
		quizType,
		enforcedProfilesOnly,
		proctoringOption,
		verityInstituteSettings,
		proctoringAssessmentConfiguration?.isLiveProctored,
		pendingLiveSessionIds.length,
		activeProfile,
		proctoringAssessmentConfiguration,
		proctoringData.thirdPartyPlatforms,
		isDraftInvalid,
		accommodationView,
		isSetupGuideWorkflow,
		loading,
	]);

	return {
		saveButtonHandler,
		closeButtonHandler,
		saveLiveSessionHandler: saveLiveSessionsHandleErrors,
	};
}
